import { event as eventGa } from '@marine/lib';
import { useQueryClient } from '@tanstack/react-query';
import { FormInstance, message } from 'antd';
import { eventNames } from 'mdc-constants';
import { useCallback } from 'react';
import { useHandleFormErrors, useUpdateFile } from 'services';
import { transformFileUpdate } from '../utils/FileForm.util';

interface FileUploadHandlerProps {
  form: FormInstance<any>;
  onSuccess?: () => void;
}

export const useFileUpdateHandler = ({ form, onSuccess = () => {} }: FileUploadHandlerProps) => {
  const { mutate: updateFile, isPending: isSavingFile } = useUpdateFile();
  const queryClient = useQueryClient();
  const { handleErrors } = useHandleFormErrors(form);

  const handleEditFile = useCallback(
    (callback?: (errors?: any) => void) => {
      const values: any = form?.getFieldsValue(true);

      const fileUpdateVariables = transformFileUpdate(values);

      form
        ?.validateFields()
        .then(() => {
          updateFile(fileUpdateVariables, {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: ['getFile', { id: fileUpdateVariables?.id }] });
              queryClient.invalidateQueries({ queryKey: ['getPaginatedFiles'] });
              message.success('File was updated successfully.');
              onSuccess();
              eventGa({
                action: eventNames.fileUpdate,
                category: eventNames.fileEventCategory,
                label: eventNames.fileUpdateLabel,
              });
            },
          });
        })
        .catch((error: any) => {
          if (callback) callback(error);
          handleErrors(error, { validationNotification: true });
        });
    },
    [form, handleErrors, updateFile, queryClient, onSuccess],
  );

  return {
    handleEditFile,
    isSavingFile,
  };
};
